import { minusToBrackets } from '@revenuemarket/utils/number/formatMoney';
import { ceil, format, round, floor } from 'mathjs';

export const formatBigNumToStr = (value: number) => {
  return format(value, { notation: 'fixed' });
};

/**
 * VC Backed  관련 데이터 표기
 * @param data
 * @returns
 */
export const formatPass = (data?: boolean, emptyValue = '-') => {
  if (data === undefined || data === null) return emptyValue;
  return data ? 'O' : 'X';
};

/**
 * 100만 단위의 숫자들은 W(X)M 으로 표기 ex) 100만 -> W1M, 1억 -> W100M
 * @param num 변환할 숫자
 * @returns
 */
export const formatWMFormat = (num?: number) => {
  if (num === undefined) return '-';

  return num >= 0
    ? `W${round(num / 1000000).toLocaleString()}M`
    : `(W${Math.abs(round(num / 1000000)).toLocaleString()}M)`;
};

export function addCommasToNumber(number: string) {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * 소수점 표기 관련 함수
 * @param number 포맷팅할 숫자
 * @param n 표기할 소수점 자릿수
 * @param option type: 반올림/올림/내림 여부, formatMinus: 음수를 괄호로 표기할지 여부, suffix: 숫자 뒤에 붙일 문자열, addComma: 3자리마다 콤마를 찍을지 여부
 * @returns
 */
export const fixedNumFormat = (
  number: number | undefined,
  n: number,
  option?: {
    type?: 'round' | 'ceil' | 'floor';
    formatMinus?: boolean;
    suffix?: string;
    addComma?: boolean;
    emptyValue?: string;
  },
) => {
  if (number === undefined || number === null) return option?.emptyValue ?? '-';

  const suffix = option?.suffix || '';
  switch (option?.type) {
    case 'round':
      const roundResult = addCommasToNumber(round(number, n).toFixed(n));

      return `${option.formatMinus ? minusToBrackets(roundResult + suffix) : roundResult + suffix}`;
    case 'ceil':
      const ceilResult = addCommasToNumber(ceil(number, n).toFixed(n));
      return `${option.formatMinus ? minusToBrackets(ceilResult + suffix) : ceilResult + suffix}`;
    case 'floor':
      const floorResult = addCommasToNumber(floor(number, n).toFixed(n));
      return `${option.formatMinus ? minusToBrackets(floorResult + suffix) : floorResult + suffix}`;
    default:
      const result = addCommasToNumber(number.toFixed(n));
      return option?.formatMinus ? minusToBrackets(result + suffix) : result + suffix;
  }
};

/**
 * 흑자,적자 관련 표기 함수
 * @param num 포맷팅할 숫자
 */
export const makeEstimatedRunwayText = (num?: number, emptyValue = '-') => {
  if (num === undefined || num === null) return emptyValue;
  return num !== undefined
    ? num >= 0
      ? `${fixedNumFormat(num, 1, {
          suffix: ' months',
        })}`
      : 'Profit-making'
    : '';
};

/**
 * percent 변환
 * @param num 포맷팅할 숫자
 * @returns
 */
export const makePecentNum = (num?: number) => {
  return num !== undefined ? num * 100 : undefined;
};

/**
 * N/A 표기 관련 함수
 * @param value
 * @returns
 */
export const makeNotAvaliable = (value?: number | string) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    return value.includes('-1') ? 'N/A' : value;
  } else {
    return value === -1 ? 'N/A' : value.toLocaleString();
  }
};

/**
 *
 * @param num 포맷팅할 숫자
 * @param standardNum 기준 값 숫자
 * @param targetFormatNum 반올림할 범위
 * @returns ex) 10만원에서 반올림해서 100만원단위로 표기 roundFormatNum(숫자, 100000, 1000000)
 */
export function roundFormatNum(
  num: number,
  standardNum: number,
  targetFormatNum: number,
  formatMinus?: boolean,
  dashOnZero?: boolean,
) {
  const roundedValue = Math.round(num / standardNum) * standardNum;

  const result = Math.round(roundedValue / targetFormatNum);

  const minusFormattedValue = formatMinus
    ? minusToBrackets(result.toLocaleString())
    : result.toLocaleString();

  return dashOnZero && num === 0 ? '-' : minusFormattedValue;
}
